<template>
  <div class="items-center w-full pl-2 pr-1 space-y-4">
    <div v-if="!activityForForm" :class="{ 'opacity-60 pointer-events-none': selectedNode.data.flowObjectType }">
      <Listbox v-model="object_type" :disabled="disabled || selectedNode.data.flowObjectType" as="div" class="relative w-full">
        <ListboxLabel
          class="block text-base font-medium text-gray-700 dark:text-dark-body-text bg-light-box-background dark:bg-dark-box-background"
          :for="name">
          {{ $t("Change metric for") }}:
        </ListboxLabel>
        <div class="relative h-full">
          <ListboxButton
            class="relative w-full h-full py-1 pl-2 pr-10 overflow-hidden text-left bg-white border rounded-md cursor-default focus:ring-dark-brand-blue dark:focus:ring-dark-brand-blue focus:border-dark-brand-blue dark:focus:border-dark-brand-blue dark:bg-dark-screen-background border-light-divider-border dark:border-dark-divider-border focus:outline-none">
            <span v-if="!object_type" class="block py-1 truncate text-thin text-common-grey dark:text-dark-body-text">{{
              $t("Select Object Type")
            }}</span>
            <span v-else class="block py-1 truncate text-thin text-light-body-text dark:text-dark-body-text">{{ object_type }}</span>
            <span class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
              <ChevronDown class="w-5 h-5 text-gray-400" aria-hidden="true" />
            </span>
          </ListboxButton>

          <transition leave-active-class="transition duration-100 ease-in" leave-from-class="opacity-100" leave-to-class="opacity-0">
            <ListboxOptions
              class="absolute z-50 w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg min-w-max dark:bg-dark-screen-background max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none text-light-body-text dark:text-dark-body-text">
              <ListboxOption
                v-for="object_type in object_type_options"
                :key="object_type"
                v-slot="{ active, selected }"
                as="template"
                :value="object_type">
                <li
                  :class="[
                    active ? 'text-white bg-blue' : 'text-light-body-text dark:text-dark-body-text',
                    'cursor-default relative py-2 pl-5 pr-4 text-left has-tooltip'
                  ]">
                  <span :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']">
                    {{ object_type }}
                  </span>

                  <span
                    v-if="selected"
                    :class="[active ? 'text-white' : 'text-blue', 'absolute inset-y-0 left-0 flex items-center pl-1.5']">
                    <Check class="w-5 h-5" aria-hidden="true" />
                  </span>
                  <div class="flex-wrap p-2 text-white bg-gray-700 rounded tooltip whitespace-nowrap">
                    {{ object_type }}
                  </div>
                </li>
              </ListboxOption>
            </ListboxOptions>
          </transition>
        </div>
      </Listbox>
    </div>

    <div class="!mt-14 relative top-2 font-medium">
      {{ $t("Select Metric to change and new value") }}:
    </div>
    <AutoCompleteInput
      v-if="$store.state.metrics"
      :class="[{ 'pointer-events-none opacity-0 absolute': activityForForm }]"
      :value="selected_metric?.name"
      :data="all_metrics"
      :disabled="disabled"
      :placeholder="$t('Search for a Metric')"
      :is-box-background="isBoxBackground"
      @chosen="chooseMetric($event)"
      @reset="resetMetric()" />

    <Listbox
      v-if="filtered_result_references.length"
      v-model="selected_metric.record"
      :class="{ 'pointer-events-none opacity-30': !selected_metric?.name }"
      :disabled="disabled"
      as="div"
      class="relative flex-1">
      <div class="relative h-full">
        <ListboxButton
          class="relative w-full h-full py-1 pl-2 pr-10 overflow-hidden text-left bg-white border rounded-md cursor-default focus:ring-dark-brand-blue dark:focus:ring-dark-brand-blue focus:border-dark-brand-blue dark:focus:border-dark-brand-blue dark:bg-dark-screen-background border-light-divider-border dark:border-dark-divider-border focus:outline-none">
          <span v-if="selected_metric?.record" class="block py-1 truncate text-thin text-light-body-text dark:text-dark-body-text">{{
            selected_metric?.record?.name
          }}</span>
          <span v-else class="block py-1 truncate text-thin text-common-grey dark:text-dark-body-text">{{ $t("Record Reference") }}</span>
          <span class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
            <ChevronDown class="w-5 h-5 text-gray-400" aria-hidden="true" />
          </span>
        </ListboxButton>

        <transition leave-active-class="transition duration-100 ease-in" leave-from-class="opacity-100" leave-to-class="opacity-0">
          <ListboxOptions
            class="absolute z-50 w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg min-w-max dark:bg-dark-screen-background max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none text-light-body-text dark:text-dark-body-text">
            <ListboxOption
              v-for="option in filtered_result_references"
              :key="option.value"
              v-slot="{ active, selected }"
              as="template"
              :value="option">
              <li
                :class="[
                  active ? 'text-white bg-blue' : 'text-light-body-text dark:text-dark-body-text',
                  'cursor-default relative py-2 pl-5 pr-4 text-left'
                ]">
                <span :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']">
                  {{ option?.name }}
                </span>

                <span v-if="selected" :class="[active ? 'text-white' : 'text-blue', 'absolute inset-y-0 left-0 flex items-center pl-1.5']">
                  <Check class="w-5 h-5" aria-hidden="true" />
                </span>
                <div class="flex-wrap p-2 text-white bg-gray-700 rounded tooltip whitespace-nowrap">
                  {{ option?.name }}
                </div>
              </li>
            </ListboxOption>
          </ListboxOptions>
        </transition>
      </div>
    </Listbox>

    <Listbox
      v-if="other_metrics.find((metric) => selected_metric?.reference_name?.includes(metric.reference_name))"
      v-model="selected_metric.option"
      :class="{ 'pointer-events-none opacity-30': !selected_metric?.name }"
      :disabled="disabled"
      as="div"
      class="relative flex-1">
      <div class="relative h-full">
        <ListboxButton
          class="relative w-full h-full py-1 pl-2 pr-10 overflow-hidden text-left bg-white border rounded-md cursor-default focus:ring-dark-brand-blue dark:focus:ring-dark-brand-blue focus:border-dark-brand-blue dark:focus:border-dark-brand-blue dark:bg-dark-screen-background border-light-divider-border dark:border-dark-divider-border focus:outline-none">
          <span v-if="selected_metric?.option" class="block py-1 truncate text-thin text-light-body-text dark:text-dark-body-text">{{
            selected_metric?.option?.label
          }}</span>
          <span v-else class="block py-1 truncate text-thin text-common-grey dark:text-dark-body-text">{{ $t("Update this field") }}</span>
          <span class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
            <ChevronDown class="w-5 h-5 text-gray-400" aria-hidden="true" />
          </span>
        </ListboxButton>

        <transition leave-active-class="transition duration-100 ease-in" leave-from-class="opacity-100" leave-to-class="opacity-0">
          <ListboxOptions
            class="absolute z-50 w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg min-w-max dark:bg-dark-screen-background max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none text-light-body-text dark:text-dark-body-text">
            <ListboxOption
              v-for="option in selected_metric?.options"
              :key="option.value"
              v-slot="{ active, selected }"
              as="template"
              :value="option"
              @click="metricSubNameChanged(option)">
              <li
                :class="[
                  active ? 'text-white bg-blue' : 'text-light-body-text dark:text-dark-body-text',
                  'cursor-default relative py-2 pl-5 pr-4 text-left'
                ]">
                <span :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']">
                  {{ option?.label }}
                </span>

                <span v-if="selected" :class="[active ? 'text-white' : 'text-blue', 'absolute inset-y-0 left-0 flex items-center pl-1.5']">
                  <Check class="w-5 h-5" aria-hidden="true" />
                </span>
                <div class="flex-wrap p-2 ml-2 text-white bg-gray-700 rounded tooltip whitespace-nowrap">
                  {{ option?.label }}
                </div>
              </li>
            </ListboxOption>
          </ListboxOptions>
        </transition>
      </div>
    </Listbox>

    <div>
      <Listbox
        v-model="selected_metric.operator"
        :class="{ 'pointer-events-none opacity-30': !selected_metric.name }"
        :disabled="disabled"
        as="div"
        class="relative w-full">
        <div class="relative h-full">
          <ListboxButton
            class="relative w-full h-full py-1 pl-2 pr-10 overflow-hidden text-left bg-white border rounded-md cursor-default focus:ring-dark-brand-blue dark:focus:ring-dark-brand-blue focus:border-dark-brand-blue dark:focus:border-dark-brand-blue dark:bg-dark-screen-background border-light-divider-border dark:border-dark-divider-border focus:outline-none">
            <span v-if="!selected_metric.operator" class="block py-1 truncate text-thin text-common-grey dark:text-dark-body-text">{{
              $t("Select Action")
            }}</span>
            <span v-else class="block py-1 truncate text-thin text-light-body-text dark:text-dark-body-text">{{
              selected_metric.operator?.label
            }}</span>
            <span class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
              <ChevronDown class="w-5 h-5 text-gray-400" aria-hidden="true" />
            </span>
          </ListboxButton>

          <transition leave-active-class="transition duration-100 ease-in" leave-from-class="opacity-100" leave-to-class="opacity-0">
            <ListboxOptions
              class="absolute z-50 w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg min-w-max dark:bg-dark-screen-background max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none text-light-body-text dark:text-dark-body-text">
              <ListboxOption
                v-for="operator in selected_metric.operators"
                :key="operator.value"
                v-slot="{ active, selected }"
                as="template"
                :value="operator">
                <li
                  :class="[
                    active ? 'text-white bg-blue' : 'text-light-body-text dark:text-dark-body-text',
                    'cursor-default relative py-2 pl-5 pr-4 text-left has-tooltip'
                  ]">
                  <span :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']">
                    {{ operator?.label }}
                  </span>

                  <span
                    v-if="selected"
                    :class="[active ? 'text-white' : 'text-blue', 'absolute inset-y-0 left-0 flex items-center pl-1.5']">
                    <Check class="w-5 h-5" aria-hidden="true" />
                  </span>
                  <div class="flex-wrap p-2 text-white bg-gray-700 rounded tooltip whitespace-nowrap">
                    {{ operator?.label }}
                  </div>
                </li>
              </ListboxOption>
            </ListboxOptions>
          </transition>
        </div>
      </Listbox>
    </div>

    <div class="w-full" :class="{ 'pointer-events-none opacity-30': !selected_metric.operator }">
      <QuarterDatePicker
        v-if="selected_metric?.operator?.value == '__DAYS_WITHIN_RANGE__'"
        v-model:current-range="selected_metric.value"
        class="w-full mr-2 bg-white rounded-lg dark:bg-dark-box-background" />

      <input
        v-if="
          (selected_metric.data_type == 'Date' || selected_metric.data_type == 'DateTime') && selected_metric?.operator?.label.includes('X')
        "
        v-model="selected_metric.value"
        :placeholder="$t('Number of days')"
        min="1"
        :disabled="disabled"
        type="number"
        class="flex-1 block w-full pl-5 my-2 overflow-hidden bg-white rounded-md border-1 focus:outline-none focus:ring-blue-light focus:border-blue-light dark:bg-dark-screen-background text-light-body-text dark:text-dark-body-text border-light-divider-border dark:border-dark-divider-border"
        @input="
          (e) =>
            $utils.debounce(() => {
              e.target.value < 1 ? (selected_metric.value = '') : selected_metric.value
            }, 600)
        ">

      <div
        v-else-if="selected_metric.data_type == 'Date' || selected_metric.data_type == 'DateTime'"
        class="z-20 w-full bg-white dark:bg-dark-screen-background">
        <DatePicker
          v-model="selected_metric.value"
          :is-dark="$store.state.ui.darkMode"
          :model-config="{
            type: 'string',
            mask: 'YYYY-MM-DD'
          }"
          class="z-50 w-full px-1 pr-2 overflow-hidden bg-white dark:bg-dark-screen-background"
          :popover="{ visibility: 'click' }">
          <template #default="{ inputValue, inputEvents }">
            <input
              :disabled="disabled"
              :value="inputValue"
              class="w-full px-2 py-2 bg-white border rounded outline-none dark:bg-dark-screen-background text-light-body-text dark:text-dark-body-text border-light-divider-border dark:border-dark-divider-border focus:ring-1 focus:ring-blue-light focus:border-blue-light"
              v-on="inputEvents">
          </template>
        </DatePicker>
      </div>

      <input
        v-else-if="selected_metric.data_type == 'Time'"
        v-model="selected_metric.value"
        type="time"
        :disabled="disabled"
        class="flex-grow w-full pl-8 pr-2 transition ease-in-out border rounded-lg outline-none text-light-body-text dark:text-dark-body-text border-light-divider-border dark:border-dark-divider-border focus:ring-1 focus:outline-none focus:ring-dark-brand-blue dark:focus:ring-dark-brand-blue focus:border-dark-brand-blue dark:focus:border-dark-brand-blue duration-side-menu-transition-duration"
        :class="[
          !isBoxBackground
            ? 'bg-light-screen-background dark:bg-dark-screen-background'
            : 'bg-light-box-background dark:bg-dark-box-background'
        ]">

      <input
        v-else-if="selected_metric.data_type == 'Number'"
        v-model="selected_metric.value"
        :placeholder="$t('Type a number')"
        :disabled="disabled"
        type="number"
        class="flex-1 block w-full pl-5 my-2 overflow-hidden bg-white rounded-md border-1 focus:outline-none focus:ring-blue-light focus:border-blue-light dark:bg-dark-screen-background text-light-body-text dark:text-dark-body-text border-light-divider-border dark:border-dark-divider-border">

      <input
        v-else-if="selected_metric.data_type == 'Boolean'"
        v-model="selected_metric.value"
        :placeholder="$t('Type true or false')"
        disabled
        type="checkbox"
        class="py-2 my-2 bg-white border rounded outline-none opacity-0 dark:bg-dark-screen-background text-light-body-text dark:text-dark-body-text border-light-divider-border dark:border-dark-divider-border focus:ring-1 focus:ring-blue-light focus:border-blue-light">

      <ComboBoxInput
        v-else-if="selected_metric.reference_name == 'tags__name'"
        v-model:model-value="selected_metric.value"
        name="tags"
        :items="$store.state.data.tags"
        label-attr="name"
        value-attr="id"
        :is-multiple="false"
        :allow-null="true"
        :return-value="true"
        label=""
        classes="w-full mr-2 relative dark:text-white"
        input-classes="sm:text-sm appearance-none font-medium text-gray-700 text-left block w-full py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light dark:border-dark-divider-border dark:bg-dark-box-background"
        selection-text=""
        :placeholder="$t('Assigned tags')"
        :floating-label="false"
        :show-label="false" />

      <ComboBoxInput
        v-else-if="
          selected_metric.reference_name == 'owner__email' ||
            selected_metric.reference_name == 'co_owners__email' ||
            (selected_metric.reference_name?.includes('_email') &&
              other_metrics.find((metric) => selected_metric.reference_name?.includes(metric.reference_name)))
        "
        v-model:model-value="selected_metric.value"
        name="owner"
        :is-multiple="selected_metric.reference_name == 'co_owners__email' || activityForForm ? true : false"
        :disabled="disabled"
        :items="$store.state.data.users"
        classes="w-full mr-2 relative dark:text-white"
        label-attr="full_name"
        value-attr="email"
        label=""
        :placeholder="$t('Team members')"
        :show-label="false"
        :floating-label="false"
        :allow-null="false">
        <template #item="{ item, active }">
          <li
            :class="[active ? 'text-white bg-blue' : 'text-gray-900 dark:text-white', 'cursor-default relative py-2 pl-4 pr-4 group/name']">
            <div class="flex items-center">
              <img
                v-if="item.avatar"
                class="inline-block rounded-full h-9 w-9 object-cover"
                :src="item.avatar"
                onerror="this.onerror=null; this.src=window.default_avatar;"
                alt="">
              <Avatar
                v-else
                :hover-effect="true"
                :first-name="item.first_name"
                :last-name="item.last_name"
                class="group-hover/name:bg-cyan-500" />
              <div class="ml-3">
                <p class="h-full text-sm font-normal text-gray-700 dark:text-white group-hover/name:text-white">
                  {{ Record.from(item).full_name }}
                </p>
                {{ item.email }}
              </div>
            </div>
          </li>
        </template>
      </ComboBoxInput>

      <ComboBoxInput
        v-else-if="
          selected_metric?.option?.options &&
            other_metrics.find((metric) => selected_metric.reference_name?.includes(metric.reference_name))
        "
        v-model:model-value="selected_metric.value"
        :disabled="disabled"
        :is-multiple="activityForForm ? false : true"
        name="owner"
        :items="selected_metric.option.options"
        classes="w-full mr-2 relative dark:text-white"
        label-attr="label"
        value-attr="value"
        label=""
        :placeholder="$t('Select item')"
        :show-label="false"
        :floating-label="false"
        :allow-null="false">
        <template #item="{ item, active }">
          <li
            :class="[active ? 'text-white bg-blue' : 'text-gray-900 dark:text-white', 'cursor-default relative py-2 pl-4 pr-4 group/name']">
            <div class="flex items-center">
              <div class="ml-3">
                <p class="h-full text-sm font-normal text-gray-700 dark:text-white group-hover/name:text-white">
                  {{ item.label }}
                </p>
              </div>
            </div>
          </li>
        </template>
      </ComboBoxInput>

      <SelectInput
        v-else-if="selected_metric?.input_type == 'Select'"
        v-model:model-value="selected_metric.value"
        :items="
          $utils.clone(
            selected_metric?.input_options?.choices.map((c, i) => {
              return { value: c, label: c, id: i }
            })
          )
        "
        name="option"
        label-attr="label"
        value-attr="value"
        :allow-null="false"
        :is-box-background="true"
        :inline-label="true"
        :show-label="false"
        :placeholder="$t('Select item')" />

      <ComboBoxInput
        v-else-if="selected_metric?.input_options?.choices"
        v-model:model-value="selected_metric.value"
        :is-multiple="true"
        :disabled="disabled"
        name="option"
        :items="
          $utils.clone(
            selected_metric?.input_options?.choices.map((c, i) => {
              return { value: c, label: c, id: i }
            })
          )
        "
        classes="w-full mr-2 relative dark:text-white"
        label-attr="label"
        value-attr="value"
        label=""
        :placeholder="$t('Select item')"
        :show-label="false"
        :floating-label="false"
        :allow-null="false">
        <template #item="{ item, active }">
          <li
            :class="[active ? 'text-white bg-blue' : 'text-gray-900 dark:text-white', 'cursor-default relative py-2 pl-4 pr-4 group/name']">
            <div class="flex items-center">
              <div class="ml-3">
                <p class="h-full text-sm font-normal text-gray-700 dark:text-white group-hover/name:text-white">
                  {{ item.label }}
                </p>
              </div>
            </div>
          </li>
        </template>
      </ComboBoxInput>

      <AutoCompleteInput
        v-else
        v-model="selected_metric.value"
        class="w-full bg-transparent border-none"
        :value="selected_metric.value"
        :endpoint-async-url="getMetricSuggestionUrl(selected_metric)"
        :is-box-background="isBoxBackground"
        :is-async="selected_metric.input_type == 'Select' && selected_metric.input_options?.choices?.length > 0 ? false : true"
        :data="
          selected_metric.input_type == 'Select' && selected_metric.input_options?.choices?.length > 0
            ? selected_metric.input_options?.choices
            : selected_metric?.suggestions
        "
        :placeholder="
          selected_metric?.operator?.value == 'in' || selected_metric?.operator?.value == '__EXCLUDES__'
            ? $t('Type word/s separated by comma')
            : $t('Type a word or a sentence')
        "
        type="text"
        classes="relative text-light-body-text dark:text-dark-body-text bg-white dark:bg-dark-screen-background rounded-md"
        input-classes="overflow-hidden  focus:outline-none focus:ring-blue-light focus:border-blue-light  w-full rounded-md border-light-divider-border dark:border-dark-divider-border"
        @changed="
          ($event) => {
            selected_metric.value = $event
          }
        "
        @chosen="
          ($event) => {
            selected_metric.value = $event
          }
        " />
    </div>
  </div>
</template>

<script>
import { ChevronDown, Check } from "lucide-vue-next"
import AutoCompleteInput from "@/components/shared/AutoCompleteInput.vue"
import { Listbox, ListboxLabel, ListboxButton, ListboxOption, ListboxOptions } from "@headlessui/vue"

import json_data from "@/components/shared/json-files/all-obj-fields.json"
import { Record } from "@/models/record"

import { DatePicker } from "v-calendar"
import ComboBoxInput from "@/components/shared/ComboBoxInput.vue"
import SelectInput from "@/components/shared/SelectInput.vue"
import Avatar from "@/components/shared/Avatar.vue"

import QuarterDatePicker from "@/components/shared/QuarterDatePicker.vue"
import { transformServerFilterToMetricFilter, transformMetricFilterToServerFilter } from "@/services/data.transformers"

export default {
  components: {
    SelectInput,
    AutoCompleteInput,
    Listbox,
    ListboxLabel,
    ListboxButton,
    ListboxOption,
    ListboxOptions,
    QuarterDatePicker,
    ComboBoxInput,
    DatePicker,
    Avatar,
    ChevronDown,
    Check
  },
  props: { selectedNode: null, isBoxBackground: null, previousLabels: null, activityForForm: null },
  emits: ["updateValues"],
  data() {
    return {
      disabled: false,
      Record,
      object_type: "company",
      forecasted_objects: [],
      other_metrics: [],
      selected_metric: {
        name: null,
        reference_name: null,
        option: null,
        options: null,
        operator: null,
        operators: null,
        value: null,
        input_options: null,
        suggestions: null,
        data_type: null,
        input_type: null,
        record: null,
        object_type: null
      },
      prevent_object_type_watcher: false,
      show_filter: false,
      server_filter: null,
      server_filter_valid: true
    }
  },
  computed: {
    filtered_result_references() {
      const list_calc = []

      this.previousLabels.forEach((l) => {
        if ((this.object_type == l.object_type || l.name.includes("Loop")) && !this.activityForForm) {
          list_calc.push(l)
        } else if (this.activityForForm && l.type == this.activityForForm) {
          list_calc.push(l)
        }
      })

      return list_calc.map((l) => {
        return { ...l, value: `\${${this.$utils.slugify(l.name)}.id}` }
      })
    },
    object_type_options() {
      return this.$store.state.object_types.map((o) => o.type)
    },
    all_metrics() {
      let forecast_metrics = []
      if (this.forecasted_objects?.includes(this.object_type)) {
        forecast_metrics = [
          {
            reference_name: `forecasts__forecast_status`,
            name: this.$t("Forecast Status"),
            data_type: "String",
            input_type: "Select",
            input_options: { choices: ["forecasted", "actual"] },
            object_type: this.object_type
          },
          {
            reference_name: `forecasts__forecast_type`,
            name: this.$t("Forecast Type"),
            data_type: "String",
            input_type: "Select",
            input_options: { choices: ["non-renewable", "renewable"] },
            object_type: this.object_type
          },
          {
            reference_name: `forecasts__forecast_value`,
            name: this.$t("Forecast Value"),
            data_type: "Number",
            input_type: "Number",
            object_type: this.object_type
          }
        ]
      }
      let owner_co_owner_filer = [
        {
          reference_name: `owner__email`,
          name: this.$t("Owner"),
          data_type: "String",
          input_type: "Text",
          object_type: this.object_type
        },
        {
          reference_name: `co_owners__email`,
          name: this.$t("Co-Owners"),
          data_type: "String",
          input_type: "Text",
          object_type: this.object_type
        }
      ]
      let tags_filter_fields = [
        {
          reference_name: `tags__name`,
          name: this.$t("Assigned tags"),
          data_type: "String",
          input_type: "Text",
          object_type: this.object_type
        }
      ]

      const metrics_calc = this.$store.state.metrics
        .concat(forecast_metrics)
        .concat(owner_co_owner_filer)
        .concat(this.other_metrics)
        .concat(tags_filter_fields)

      let metrics_calc_clean = []
      if (!this.activityForForm) {
        metrics_calc_clean = metrics_calc.filter(
          (m) =>
            !m.reference_name.includes("owner_email") &&
            !m.reference_name.includes("owner_first_name") &&
            !m.reference_name.includes("owner_last_name") &&
            !m.reference_name.includes("owner_name") &&
            m.data_type !== "Plan" &&
            m.data_type !== "Goal" &&
            m.data_type !== "Task" &&
            m.data_type !== "Interaction" &&
            m.data_type !== "Milestone"
        )
      }

      if (this.activityForForm) {
        metrics_calc_clean = metrics_calc.filter((m) => m.data_type?.toLowerCase() == this.activityForForm?.toLowerCase())
      }

      return metrics_calc_clean
    }
  },
  watch: {
    selected_metric: {
      deep: true,
      handler() {
        this.handleUpdateValues()
      }
    },
    object_type() {
      if (!this.prevent_object_type_watcher) {
        this.resetMetric()
      }
    },
    server_filter: {
      deep: true,
      handler() {
        this.handleUpdateValues()
      }
    }
  },

  created() {
    this.init()
  },

  methods: {
    chooseMetric(e) {
      let metric = { ...e }
      metric = this.injectOperators(metric)

      this.selected_metric = metric
    },

    injectOperators(metric) {
      let type = metric.data_type?.toLowerCase()
      if (type == "datetime" || type == "date") {
        type = "date"
      }
      let value = ""
      // For boolean the value is always true, but the operator
      // can be == or != ie: == true or != true which is false
      if (type == "boolean") {
        value = "true"
      }

      metric["operators"] = this.$store.state.data.metric_action_options.filter((option) =>
        option.for.split("_").includes(metric.data_type.toLowerCase())
      )
      metric["operator"] = null
      metric["value"] = value
      metric["item_type"] = "metric"
      return metric
    },
    resetMetric() {
      this.selected_metric = {
        name: null,
        reference_name: null,
        option: null,
        options: null,
        operator: null,
        operators: null,
        value: null,
        input_options: null,
        suggestions: null,
        data_type: null,
        input_type: null,
        object_type: null
      }
    },
    getMetricSuggestionUrl(metric) {
      return `/api/organizations/${this.$store.state.organization.id}/metrics/${metric.id}/suggestions/`
    },

    metricSubNameChanged(option) {
      setTimeout(() => {
        const metric = this.selected_metric
        let arr = metric.reference_name.split("__")
        metric.reference_name = `${arr[0] + "__"}`.concat(option.value)
        metric.data_type = option.data_type
        metric.input_type = option.input_type
      }, 600)
    },

    handleUpdateValues() {
      let valid_calc = true
      const metric_server_filter = this.selected_metric ? transformMetricFilterToServerFilter([this.selected_metric]) : ""
      if (!metric_server_filter.valid || !this.selected_metric) {
        valid_calc = false
      }

      if (this.server_filter && !this.server_filter_valid) {
        valid_calc = false
      }

      const data = {
        ...this.selectedNode.data.values,
        filtered_records_filter: this.server_filter,
        metric_filter: metric_server_filter?.filter,
        selected_object_type: this.object_type,
        record: this.selected_metric?.record?.value,
        object_type: this.selected_metric?.object_type,
        valid: valid_calc
      }
      this.$emit("updateValues", data)
    },

    init() {
      if (
        this.selectedNode.data.flowObjectType ||
        this.selectedNode.data?.values?.object_type ||
        this.selectedNode.data?.values?.selected_object_type
      ) {
        this.prevent_object_type_watcher = true
        this.object_type =
          this.selectedNode.data.flowObjectType ||
          this.selectedNode.data?.values?.object_type ||
          this.selectedNode.data?.values?.selected_object_type
        setTimeout(() => {
          this.prevent_object_type_watcher = false
        }, 300)
      }

      this.server_filter = this.selectedNode.data.values.filtered_records_filter

      if (this.selectedNode.data?.flowObjectTypeFull?.includes("child")) {
        this.show_filter = true
      }

      if (!this.selectedNode.data?.flowObjectTypeFull) {
        this.show_filter = true
      }

      this.forecasted_objects = this.$utils
        .find(this.$store.state.settings.modules_settings, {
          reference_code: "renewal_base_metric"
        })
        ?.mapped_objects_metrics?.map((type_map) => {
          return this.$store.state.object_types.find((x) => x.id == type_map.object_type)?.type
        })
      Object.entries(json_data).map((item) => {
        this.other_metrics.push({ ...item[1], object_type: this.object_type })
      })

      if (this.selectedNode.data?.values?.metric_filter) {
        this.selected_metric = transformServerFilterToMetricFilter(this.selectedNode.data.values.metric_filter, true, true).filter[1]
      }

      if (this.selectedNode.data?.values?.record) {
        this.selected_metric.record = this.filtered_result_references.find((r) => r.value == this.selectedNode.data?.values?.record)
      }

      if (this.activityForForm && !this.selectedNode.data?.values?.metric_filter) {
        const metric_calc = this.all_metrics.find((m) => m.name.toLowerCase() == this.activityForForm.replaceAll("_", " "))
        this.chooseMetric(metric_calc, 0, 0)
      }
    }
  }
}
</script>
